import { useMemo, useState } from 'react';
import { FilterBar } from '@/components/common/FilterBar';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { marginControlApi } from '@/api/services/marginControl';
import type { FetchMarginRateHistoryInput, MarginRateHistoryEventDto } from '@/api/types/marginControl';
import { fmtDateTime, todayEnd, todayStart } from '../helpers';

export function HistoryTab() {
  const [range, setRange] = useState({ start: todayStart(), end: todayEnd() });

  const extra = useMemo<FetchMarginRateHistoryInput>(
    () => ({ startDate: range.start, endDate: range.end, sorting: 'occurredAt desc' }),
    [range],
  );
  const table = useServerDataTable<MarginRateHistoryEventDto, FetchMarginRateHistoryInput>({
    queryKey: ['margin-control', 'history'],
    fetcher: (req) => marginControlApi.getHistory(req),
    pageSize: 5,
    extraParams: extra,
  });

  const columns: DataGridColumn<MarginRateHistoryEventDto>[] = [
    { key: 'occurredAt', title: 'Date Time', className: 'text-start', value: (r) => r.occurredAt, cell: (r) => fmtDateTime(r.occurredAt) },
    { key: 'eventTypeLabel', title: 'Event', className: 'text-start', value: (r) => r.eventTypeLabel },
    { key: 'summary', title: 'Summary', className: 'text-start', value: (r) => r.summary },
    { key: 'actor', title: 'Actor', className: 'text-start', value: (r) => r.actor },
  ];

  return (
    <div>
      <FilterBar
        card={false}
        className="mb-3"
        fields={[
          {
            node: (
              <div key="date">
                <label className="form-label">Date Range</label>
                <DateRangePicker mode="range" maxDate={todayEnd()} value={range} onChange={(v) => { table.setPageIndex(0); setRange(v); }} />
              </div>
            ),
            pinned: true,
            width: 270,
          },
        ]}
      />

      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => r.id}
        card
        includeExcel
        excelFileName="Margin Rate History"
        columnVisibilityToggle={false}
        emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
        server={{ pageIndex: table.pageIndex, pageSize: table.pageSize, pageCount: table.pageCount, totalCount: table.totalCount, onPageChange: table.setPageIndex, onPageSizeChange: table.setPageSize }}
      />
    </div>
  );
}
